import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import MobileLayout from '../../components/MobileLayout';
import apiService from '../../../services/api';
import { getBaseVideoList } from '../../../services/video';
import './MobileHome.css';

const IconVideo = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="15" height="10" rx="2"/><polyline points="17 10 22 7 22 17 17 14"/></svg>;
const IconUser  = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconMic   = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 013 3v8a3 3 0 01-6 0V4a3 3 0 013-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>;
// Video player icon: rounded rect with centered play triangle
const IconFilm  = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2"/><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/></svg>;

const SUPPORT_QR_FALLBACK = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://work.weixin.qq.com/ct/wcde3d12f7814a242db34db2e2584a12fdc9')}`;

const IconHeadset = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/>
    <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
  </svg>
);
const IconClose = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// countKey maps to counts state; countCls drives badge color
const QUICK_ACTIONS = [
  { to: '/m/generate', label: '生成视频', desc: '快速合成数字人视频', iconCls: 'mhome-action-icon--blue',   countCls: '',         countKey: null,     Icon: IconVideo },
  { to: '/m/avatars',  label: '我的形象', desc: '管理数字人形象库',  iconCls: 'mhome-action-icon--green',  countCls: '--green',  countKey: 'avatars', Icon: IconUser  },
  { to: '/m/voices',   label: '我的声音', desc: '管理声音克隆库',    iconCls: 'mhome-action-icon--purple', countCls: '--purple', countKey: 'voices',  Icon: IconMic   },
  { to: '/m/tasks',    label: '我的视频', desc: '生成任务记录',      iconCls: 'mhome-action-icon--orange', countCls: '--orange', countKey: 'videos',  Icon: IconFilm  },
];

export default function MobileHome() {
  const { user } = useAuth();

  const [credits, setCredits]       = useState(null);
  const [counts, setCounts]         = useState({ avatars: null, voices: null, videos: null });
  const [showSupport, setShowSupport] = useState(false);

  // Fetch available credits
  useEffect(() => {
    fetch('/api/billing/benefits', { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (data.code === 0 && data.data) {
          const total = Number(data.data.total)     || 0;
          const used  = Number(data.data.totalUsed) || 0;
          setCredits({ total, used, remaining: total - used });
        }
      })
      .catch(() => {});
  }, []);

  // Fetch library counts (page=1, size=1 — we only need the total field)
  useEffect(() => {
    if (!user?.phone) return;
    const extractApiTotal = (res) => {
      const d = res.status === 'fulfilled' ? res.value : null;
      if (!d || d.code !== 0) return null;
      return d?.data?.total ?? d?.data?.totalCount ?? d?.total ?? d?.totalCount ?? null;
    };
    Promise.allSettled([
      apiService.getMyAvatars(1, 1, ''),
      apiService.getMyVoices(1, 1, ''),
      getBaseVideoList({ phone: user.phone, page: 1, pageSize: 10 }),
    ]).then(([a, v, vid]) => {
      // videos response: { videos: [...], total: N, ... }
      const vidData = vid.status === 'fulfilled' ? vid.value : null;
      const videoTotal = vidData
        ? Number(vidData?.total ?? vidData?.data?.total ?? 0) || null
        : null;
      setCounts({
        avatars: extractApiTotal(a),
        voices:  extractApiTotal(v),
        videos:  videoTotal,
      });
    });
  }, [user?.phone]);

  const phone = user?.phone || '';
  const maskedPhone = phone.length >= 11
    ? `${phone.slice(0, 3)}****${phone.slice(-4)}`
    : phone;

  const fmt = (v) => new Intl.NumberFormat('zh-CN').format(Number.isFinite(v) ? v : 0);
  const remaining = credits?.remaining ?? null;
  const total     = credits?.total     ?? null;
  const used      = credits?.used      ?? null;
  const pct = (total > 0 && remaining != null)
    ? Math.min(100, Math.round((remaining / total) * 100))
    : null;

  return (
    <MobileLayout
      title="PSYCHE-AI"
    >
      <div className="mhome-root">
        {/* Welcome banner */}
        <div className="mhome-banner">
          <div className="mhome-banner-greeting">你好，{maskedPhone}</div>
          <div className="mhome-banner-sub">开始创作你的数字人视频</div>

          {/* Credits widget */}
          <div className="mhome-credits-widget">
            <div className="mhome-credits-balance-row">
              <span className="mhome-credits-avail">{remaining != null ? fmt(remaining) : '—'}</span>
              <span className="mhome-credits-slash"> / </span>
              <span className="mhome-credits-total-num">{total != null ? fmt(total) : '—'}</span>
              <span className="mhome-credits-unit">积分</span>
            </div>
            <div className="mhome-credits-bar-track">
              <div
                className="mhome-credits-bar-fill"
                style={{ width: pct != null ? `${pct}%` : '0%' }}
              />
            </div>
            <div className="mhome-credits-bar-label">
              可用额度 {pct != null ? `${pct}%` : '—'}
            </div>
            <Link to="/m/bills" className="mhome-credits-detail-link">
              <span>查看积分明细</span>
              <span className="mhome-credits-detail-arrow">›</span>
            </Link>
          </div>
        </div>

        {/* Quick actions */}
        <div className="mhome-section-title">快捷操作</div>
        <div className="mhome-actions">
          {QUICK_ACTIONS.map(({ to, label, desc, iconCls, countCls, countKey, Icon }) => {
            const count = countKey != null ? counts[countKey] : null;
            return (
              <Link key={to} to={to} className="mhome-action-card">
                {/* Top row: icon left, "共 X 个" right — same vertical alignment */}
                <div className="mhome-action-top">
                  <div className={`mhome-action-icon ${iconCls}`}><Icon /></div>
                  {countKey && (
                    <div className={`mhome-action-count-tag${countCls}`}>
                      {count != null ? `共 ${count} 个` : '—'}
                    </div>
                  )}
                </div>
                <div className="mhome-action-label">{label}</div>
                <div className="mhome-action-desc">{desc}</div>
              </Link>
            );
          })}
        </div>

        {/* Support entry */}
        <div className="mhome-support-btn" onClick={() => setShowSupport(true)}>
          <div className="mhome-support-icon"><IconHeadset /></div>
          <div className="mhome-support-text">
            <div className="mhome-support-label">联系客服</div>
            <div className="mhome-support-hint">遇到问题？微信扫码联系我们</div>
          </div>
          <div className="mhome-support-arrow">›</div>
        </div>

        {/* QR modal */}
        {showSupport && (
          <div className="mhome-modal-overlay" onClick={() => setShowSupport(false)}>
            <div className="mhome-modal-card" onClick={(e) => e.stopPropagation()}>
              <button className="mhome-modal-close" onClick={() => setShowSupport(false)}>
                <IconClose />
              </button>
              <div className="mhome-modal-title">联系客服</div>
              <img
                className="mhome-modal-qr"
                src="/static/images/support-qr.png"
                alt="客服二维码"
                onError={(e) => { e.currentTarget.src = SUPPORT_QR_FALLBACK; }}
              />
              <p className="mhome-modal-hint">
                截图保存二维码<br />在微信中扫码添加客服为好友
              </p>
            </div>
          </div>
        )}
      </div>
    </MobileLayout>
  );
}
